<template>
  <ApiResourceCard :name="integration.name">
    <template #actions>
      <ApiResourceCardActions
        action-text="Settings"
        :action-icon="['fak', 'settings']"
        :resource-name="integration.name"
        @action="emit('settings')"
      />
    </template>
    <ApiMetaList>
      <ApiMetaItem
        v-for="item in metaItems"
        :key="item.label"
        :label="item.label"
        :value="item.value"
      >
        <ApiCopyValue
          v-if="item.copyLabel"
          :value="item.value"
          :label="item.copyLabel"
        />
      </ApiMetaItem>
    </ApiMetaList>
  </ApiResourceCard>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { computed } from 'vue';

import type { OauthApplication } from '@/types/oauth_applications';

import ApiCopyValue from './ApiCopyValue.vue';
import ApiMetaItem from './ApiMetaItem.vue';
import ApiMetaList from './ApiMetaList.vue';
import ApiResourceCard from './ApiResourceCard.vue';
import ApiResourceCardActions from './ApiResourceCardActions.vue';

const { integration } = defineProps<{
  integration: OauthApplication;
}>();

const emit = defineEmits<{
  settings: [];
}>();

const createdLabel = computed(() =>
  DateTime.fromISO(integration.created_at).toLocaleString(DateTime.DATE_MED),
);

const lastUsedLabel = computed(() => {
  if (!integration.last_used_at) return 'Never';
  const relative = DateTime.fromISO(integration.last_used_at).toRelative();
  return !relative || relative.startsWith('in') ? 'Just now' : relative;
});

const callbacksLabel = computed(() =>
  integration.callbacks.length > 1 ? 'Callback URLs' : 'Callback URL',
);

const callbacksValue = computed(
  () => integration.callbacks.join(', ') || 'None',
);

const metaItems = computed(() => [
  {
    label: 'Client ID',
    value: integration.client_id,
    copyLabel: `client ID for ${integration.name}`,
  },
  { label: 'Created', value: createdLabel.value },
  { label: 'Last used', value: lastUsedLabel.value },
  { label: callbacksLabel.value, value: callbacksValue.value },
]);
</script>
